import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRetailerAuth } from './RetailerAuthContext';
import {
  Home,
  TrendingUp,
  Maximize2,
  Sliders,
  Layers,
  Grid,
  ShoppingCart,
  MessageSquare,
  Eye,
  ImagePlus,
  QrCode,
  Calendar,
  CreditCard,
  LogOut,
  Settings,
  User,
  FileText,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import colors from '../../theme/colors';

export default function RetailerSidebar() {
  const navigate = useNavigate();
  const { logout } = useRetailerAuth();
  const location = useLocation();
  const [expandedMenu, setExpandedMenu] = useState({ 'visualizer-settings': true });

  const menuGroups = [
  {
    title: 'HOME',
    items: [
      { id: 'dashboard', label: 'Dashboard', path: '/retailer/dashboard', icon: Home },
    ]
  },
  {
    title: 'PRODUCT MANAGEMENT',
    items: [
      { id: 'product-size', label: 'Product Size', path: '/retailer/product-sizes', icon: Maximize2, badge: '', badgeType: 'count' },
      { id: 'product-design-sku', label: 'Product Design SKU', path: '/retailer/tiles', icon: Grid },

    ]
  },
  {
    title: 'INQUIRIES MANAGEMENT',
    items: [
      { id: 'product-inquiries', label: 'Product Inquiries', path: '/retailer/product-inquiries', icon: ShoppingCart, badge: '', badgeType: 'new' },
      // { id: 'messages', label: 'Messages', path: '/retailer/messages', icon: MessageSquare },
    ]
  },
  {
    title: 'ORDER MANAGEMENT',
    items: [
      { id: 'order-history', label: 'Order History', path: '/retailer/order-history', icon: ShoppingCart, badge: '', badgeType: 'new' },
      // { id: 'messages', label: 'Messages', path: '/retailer/messages', icon: MessageSquare },
    ]
  },
  {
    title: 'SYSTEM SETTINGS',
    items: [
      {
        id: 'visualizer-settings',
        label: 'Visualizer Settings',
        path: '/retailer/settings/visualizer',
        icon: Settings,
        subItems: [
          { id: 'preview-setting', label: 'Preview Setting', path: '/retailer/settings/visualizer/preview' },
          // { id: 'catalog-setting', label: 'Catalog Setting', path: '/retailer/settings/visualizer/catalog' },
          // { id: 'manage-features', label: 'Manage Features', path: '/retailer/settings/visualizer/features' },
          // { id: 'customize-visualizer', label: 'Customize Visualizer', path: '/retailer/settings/visualizer/customize' },
          // { id: 'grout-layout-settings', label: 'Grout & Layout Settings', path: '/retailer/settings/visualizer/grout-layout' },
          // { id: 'visitor-preview-form', label: 'Visitor & Preview Form', path: '/retailer/settings/visualizer/visitor-form' },
          // { id: 'manage-product-labels', label: 'Manage Product Labels', path: '/retailer/settings/visualizer/product-labels' },
        ]
      },
      {
        id: 'account-settings',
        label: 'Account Settings',
        path: '/retailer/settings/account',
        icon: User,
        subItems: []
      },
      // {
      //   id: 'log-management',
      //   label: 'Log Management',
      //   path: '/retailer/settings/logs',
      //   icon: FileText,
      //   subItems: []
      // }
    ]
  }
];

  const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

  const handleLogout = async () => {
    await logout();   // POSTs /logout, clears the cookie, resets context state
    navigate('/');
  };



  const toggleSubmenu = (id) => {
    setExpandedMenu((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  return (
    <aside className="w-64 bg-white border-r border-gray-100 fixed h-full top-0 left-0 z-50 flex flex-col justify-between overflow-y-auto font-sans">
      <div className="py-6 px-4">
        {/* Header / Logo */}
        <div className="px-3 mb-6">
          <Link to="/retailer/dashboard" className="flex items-center gap-2 font-bold text-xl tracking-tight text-gray-900">
            <span className="text-2xl font-extrabold tracking-tight" style={{ color: colors.accent }}>
                          nitya<span className="text-orange-500">.ai</span>
                        </span>
          </Link>
        </div>

        {/* Navigation Sections */}
        <div className="space-y-6">
          {menuGroups.map((group, index) => (
            <div key={index}>
              <h3 className="px-3 text-xs font-bold text-gray-400 tracking-wider uppercase mb-2">
                {group.title}
              </h3>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const hasSubItems = item.subItems && item.subItems.length > 0;
                  const isExpanded = expandedMenu[item.id];
                  const isActive = location.pathname === item.path || 
                    (hasSubItems && item.subItems.some((sub) => location.pathname === sub.path));

                  return (
                    <div key={item.id} className="space-y-1">
                      {/* Parent Item */}
                      {hasSubItems ? (
                        <button
                          type="button"
                          onClick={() => toggleSubmenu(item.id)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all font-medium ${
                            isActive
                              ? 'text-indigo-600'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-5 h-5 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                            <span>{item.label}</span>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-indigo-500" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      ) : (
                        <Link
                          to={item.path}
                          className={`flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all font-medium ${
                            isActive
                              ? 'bg-indigo-50 text-indigo-600'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <Icon className={`w-5 h-5 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                            <span>{item.label}</span>
                          </div>

                          {/* Badge rendering */}
                          {item.badge && (
                            <span
                              className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                                item.badgeType === 'new'
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-indigo-500 text-white min-w-[20px] text-center'
                              }`}
                            >
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      )}

                      {/* Sub-items list (Accordion dropdown) */}
                      {hasSubItems && isExpanded && (
                        <div className="pl-9 pr-2 space-y-1 py-1">
                          {item.subItems.map((subItem) => {
                            const isSubActive = location.pathname === subItem.path;
                            return (
                              <Link
                                key={subItem.id}
                                to={subItem.path}
                                className={`block px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                  isSubActive
                                    ? 'text-indigo-600 font-semibold'
                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50'
                                }`}
                              >
                                {subItem.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer / Logout Button */}
      <div className="p-4 border-t border-gray-100">
        <button onClick={handleLogout} className="...">
         <LogOut className="w-5 h-5" />
         <span>Logout</span>
       </button>
      </div>
    </aside>
  );
}